<template>
  <div class="page-wrapper">
    <h2 class="tw-mb-6">Übersicht - Umsatz</h2>

    <div class="tw-w-full tw-grid tw-grid-cols-1 md:tw-grid-cols-5 tw-gap-2 md:tw-gap-4 xl:tw-gap-6 tw-mb-6" style="max-width: 1096px;">
      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="fox-label--simple">
          Suchen
        </label>
        <form class="tw-relative" @submit.prevent="fetchDocuments()">
          <input
            type="text"
            v-model="searchParams.keyword"
            class="fox-input--simple fox-input--simple-search tw-w-full tw-bg-white"
          >

          <div class="tw-absolute tw-inset-y-0 tw-right-2 tw-flex tw-items-center">
            <v-icon class="tw-cursor-pointer" @click="fetchDocuments()">mdi-magnify</v-icon>
          </div>
        </form>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="fox-label--simple">
          Kunde
        </label>
        <div class="tw-relative">
          <select v-model="searchParams.clientUuid" @change="fetchDocuments()" class="fox-input--simple fox-input--simple-select tw-w-full tw-bg-white tw-capitalize">
            <option selected :value="null">Kunde auswählen</option>
            <option v-for="client in clients" :key="client.uuid" :value="client.uuid">
              {{ client.name }}
            </option>
          </select>

          <div class="tw-absolute tw-inset-y-0 tw-right-1 tw-flex tw-items-center tw-pointer-events-none">
            <v-icon>mdi-menu-down</v-icon>
          </div>
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="fox-label--simple">
          Rechnungsart
        </label>
        <div class="tw-relative">
          <select v-model="searchParams.type" @change="fetchDocuments()" class="fox-input--simple fox-input--simple-select tw-w-full tw-bg-white tw-capitalize">
            <option selected :value="null">Rechnungsart auswählen</option>
            <option v-for="type in typeFilters" :key="type.value" :value="type.value">
              {{ type.text }}
            </option>
          </select>

          <div class="tw-absolute tw-inset-y-0 tw-right-1 tw-flex tw-items-center tw-pointer-events-none">
            <v-icon>mdi-menu-down</v-icon>
          </div>
        </div>
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="fox-label--simple">
          Zeitraum
        </label>
        <date-picker
          v-model:value="searchParams.date"
          @clear="clearDate"
          @change="fetchDocuments()"
          type="date"
          format="DD.MM.YYYY"
          value-type="YYYY-MM-DD"
        />
      </div>

      <div class="tw-flex tw-flex-col tw-gap-1">
        <label class="fox-label--simple">
          Status
        </label>
        <div class="tw-relative">
          <select v-model="searchParams.status" @change="fetchDocuments()" class="fox-input--simple fox-input--simple-select tw-w-full tw-bg-white tw-capitalize">
            <option selected :value="null">Status auswählen</option>
            <option v-for="status in statusFilters" :key="status.value" :value="status.value">
              {{ status.text }}
            </option>
          </select>

          <div class="tw-absolute tw-inset-y-0 tw-right-1 tw-flex tw-items-center tw-pointer-events-none">
            <v-icon>mdi-menu-down</v-icon>
          </div>
        </div>
      </div>
    </div>

    <div class="table-block tw-flex tw-flex-col md:tw-flex-row tw-flex-wrap tw-gap-x-8 tw-gap-y-6 tw-mb-6" style="padding: 24px;" v-if="invoiceStats">
      <div class="tw-flex tw-items-center tw-gap-4">
        <div class="tw-w-12 tw-h-12 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100">
          <CashIcon />
        </div>

        <div class="tw-flex tw-flex-col">
          <span class="tw-text-gray-500">Gesamt</span>
          <span class="tw-text-gray-900 tw-text-xl tw-font-bold">{{ invoiceStats.total?.total || 0 }}{{ invoiceStats.unit }}</span>
        </div>
      </div>

      <div class="tw-flex tw-items-center tw-gap-4">
        <div class="tw-w-12 tw-h-12 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100">
          <WarningIcon />
        </div>

        <div class="tw-flex tw-flex-col">
          <span class="tw-text-gray-500">Umsatzsteuer</span>
          <span class="tw-text-gray-900 tw-text-xl tw-font-bold">{{ invoiceStats.total?.totalTax || 0 }}{{ invoiceStats.unit }}</span>
        </div>
      </div>

      <div class="tw-flex tw-items-center tw-gap-4">
        <div class="tw-w-12 tw-h-12 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100">
          <CheckIcon />
        </div>

        <div class="tw-flex tw-flex-col">
          <span class="tw-text-gray-500">Bezahlt</span>
          <span class="tw-text-xl tw-font-bold" style="color: #0DAC50;">{{ invoiceStats.total?.totalPaid || 0 }}{{ invoiceStats.unit }}</span>
        </div>
      </div>

      <div class="tw-flex tw-items-center tw-gap-4">
        <div class="tw-w-12 tw-h-12 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100">
          <HourglassIcon />
        </div>

        <div class="tw-flex tw-flex-col">
          <span class="tw-text-gray-500">Offen</span>
          <span class="text-gradient tw-text-xl tw-font-bold">{{ invoiceStats.total?.totalOpen || 0 }}{{ invoiceStats.unit }}</span>
        </div>
      </div>

      <div class="tw-flex tw-items-center tw-gap-4">
        <div class="tw-w-12 tw-h-12 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100">
          <WalletIcon />
        </div>

        <div class="tw-flex tw-flex-col">
          <span class="tw-text-gray-500">MRR</span>
          <span class="tw-text-gray-900 tw-text-xl tw-font-bold">{{ invoiceStats.total?.mrr || 'keine Angabe' }}</span>
        </div>
      </div>

      <div class="tw-flex tw-items-center tw-gap-4">
        <div class="tw-w-12 tw-h-12 tw-flex tw-items-center tw-justify-center tw-rounded-full tw-bg-gray-100">
          <WalletIcon />
        </div>

        <div class="tw-flex tw-flex-col">
          <span class="tw-text-gray-500">ARR</span>
          <span class="tw-text-gray-900 tw-text-xl tw-font-bold">{{ invoiceStats.total?.arr || 'keine Angabe' }}</span>
        </div>
      </div>
    </div>

    <div class="table-block tw-mb-6" v-if="invoiceStats">
      <div class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-4 tw-pb-4 tw-border-b tw-border-gray-200">
        <div class="tw-flex tw-flex-col tw-gap-1">
          <h3 class="tw-text-xl tw-font-bold">Ihre Einnahmen</h3>
          <span class="tw-text-gray-500">Übersicht über Ihre aktuellen und vergangenen Einnahmen</span>
        </div>

        <!-- Tab triggers -->
        <div class="tw-flex tw-gap-4 tw-flex-wrap sm:tw-flex-nowrap">
          <button
            :class="['tw-font-medium', { 'btn-gradient btn-gradient--small': invoiceRange === 'week', 'btn-white btn-white--small': invoiceRange !== 'week' }]"
            @click="switchTab('week')"
          >
            Woche
          </button>

          <button
            :class="['tw-font-medium', { 'btn-gradient btn-gradient--small': invoiceRange === 'month', 'btn-white btn-white--small': invoiceRange !== 'month' }]"
            @click="switchTab('month')"
          >
            Monat
          </button>

          <button
            :class="['tw-font-medium', { 'btn-gradient btn-gradient--small': invoiceRange === 'year', 'btn-white btn-white--small': invoiceRange !== 'year' }]"
            @click="switchTab('year')"
          >
            Jahr
          </button>
        </div>
      </div>

      <div class="tw-inline-block tw-text-sm tw-font-medium tw-py-3" style="color: #0DAC50; border-bottom: 1px solid #0DAC50;">Einnahmen</div>

      <div class="tw-pt-8 tw-pb-4">
        <CashflowChart :range="invoiceStats.range" :unit="invoiceStats.unit" />
      </div>
    </div>

    <div class="table-block">
      <div class="tw-flex tw-items-start tw-gap-6 tw-mb-6">
        <div class="tw-flex-1 tw-flex tw-flex-col">
          <h3 class="tw-text-xl tw-font-bold tw-mb-3">Letzte Rechnungen</h3>

          <div class="tw-flex tw-flex-wrap tw-gap-x-6 tw-gap-y-1">
            <span class="text-link tw-cursor-pointer tw-font-bold" @click="selectAllInvoices">alle auswählen</span>

            <span class="text-link" v-if="selectedInvoicesUuids.length > 0">{{ selectedInvoicesUuids.length }} ausgewählt</span>

            <span class="text-link tw-cursor-pointer tw-font-bold" @click="updateInvoiceStatus('PAID')">als bezahlt markieren</span>
            <span class="text-link tw-cursor-pointer tw-font-bold" @click="updateInvoiceStatus('OPEN')">als offen markieren</span>
            <span class="text-link tw-cursor-pointer tw-font-bold" @click="updateInvoiceStatus('CANCELED')">stornieren</span>
            <span class="text-link tw-cursor-pointer tw-font-bold" @click="updateInvoiceStatus('OVERDUE')">löschen</span>
          </div>
        </div>

        <button
          v-if="selectedInvoicesUuids.length > 0"
          @click="exportPdf"
          class="fox-invoice-action__create-btn fox-invoice-action__create-btn--purple fox-invoice-action__create-btn--purple--small">
          <DownloadIcon />
          <span class="tw-text-black">Export - PDF</span>
        </button>
      </div>

      <div class="app-table-wrapper">
        <table class="app-table">
          <thead>
            <tr>
              <th>RECHNUNG / Kunde</th>
              <th>RG-NR.</th>
              <th>RG-DATUM</th>
              <th>ZAHLUNGSFRIST</th>
              <th>RG-ART</th>
              <th>Status</th>
              <th>Preis (Netto)</th>
              <th>Ust.</th>
              <th>PREIS (BRUTTO)</th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="invoice in invoices" :key="invoice.uuid">
              <td>
                <div class="tw-flex tw-items-center tw-gap-6" style="min-height: 44px;">
                  <label class="custom-checkbox">
                    <input
                      type="checkbox"
                      v-model="selectedInvoicesUuids"
                      :value="invoice.uuid"
                    />
                    <span class="checkmark"></span>
                  </label>

                  <div class="tw-flex tw-flex-col tw-text-left">
                    <span class="tw-inline-block tw-font-medium tw-truncate" style="max-width: 180px;">{{ invoice.client?.name || '' }}</span>
                    <span class="tw-inline-block tw-text-gray-500 tw-truncate" style="max-width: 180px;">{{ invoice.client?.company || '' }}</span>
                  </div>
                </div>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap">{{ invoice.number }}</span>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap">{{ invoice.createdAt }}</span>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap">{{ invoice.deadlineDate }}</span>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap" :class="{ 'tw-text-green-500': invoice.subscription?.value }">{{ invoice.type?.text || '' }}</span>
              </td>

              <td>
                <div class="tw-relative">
                  <select
                    class="app-table-select tw-capitalize"
                    :class="{
                      'app-table-select--open': invoice.status.value === 'OPEN',
                      'app-table-select--active': invoice.status.value === 'PAID',
                      'app-table-select--error': invoice.status.value === 'OVERDUE'
                    }"
                    :value="invoice.status.value"
                    @change="updateInvoiceStatus($event.target.value, invoice.uuid)"
                  >
                    <option v-for="status in statusFilters" :key="status.value" :value="status.value" :selected="invoice.status.value === status.value">
                      {{ status.text }}
                    </option>
                  </select>

                  <div class="tw-absolute tw-inset-y-0 tw-right-1 tw-flex tw-items-center tw-pointer-events-none">
                    <v-icon>mdi-menu-down</v-icon>
                  </div>
                </div>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap">{{ invoice.netto }} {{ invoice.unit }}</span>
              </td>

              <td>
                <span class="tw-font-medium tw-whitespace-nowrap">{{ invoice.tax }} {{ invoice.unit }}</span>
              </td>

              <td>
                <span class="tw-font-bold tw-whitespace-nowrap">{{ invoice.brutto }} {{ invoice.unit }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="tw-mt-8 tw-mb-4">
        <AppPagination
          v-if="totalCount > 0"
          :count="totalCount"
          :currentPage="currentPage"
          @update="(page) => fetchDocuments(page)"
        />
      </div>
    </div>

    <v-snackbar
      v-model="showMessage"
      location="top"
    >
      Datei nicht gefunden
      <template #actions>
        <v-btn
          color="error"
          @click="showMessage = false"
        >
          Schließen
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import ApiService from '@/services/api.service'
import JSZip from 'jszip'
import DatePicker from 'vue-datepicker-next'
import AppPagination from '@/components/Common/AppPagination.vue'
import DownloadIcon from '@/components/Icon/DownloadIcon.vue'
import CashIcon from '@/components/Icon/CashIcon.vue'
import WalletIcon from '@/components/Icon/WalletIcon.vue'
import WarningIcon from '@/components/Icon/WarningIcon.vue'
import CheckIcon from '@/components/Icon/CheckIcon.vue'
import HourglassIcon from '@/components/Icon/HourglassIcon.vue'
import CashflowChart from '@/components/Cashflow/CashflowChart.vue'

export default {
  name: 'CashflowView',
  components: {
    DatePicker,
    AppPagination,
    DownloadIcon,
    CashIcon,
    WalletIcon,
    WarningIcon,
    CheckIcon,
    HourglassIcon,
    CashflowChart
  },
  data () {
    return {
      clients: null,
      statusFilters: null,
      typeFilters: null,
      searchParams: {
        keyword: '',
        status: null,
        clientUuid: null,
        date: null,
        type: null
      },
      currentPage: 1,
      totalCount: 0,
      invoices: [],
      selectedInvoicesUuids: [],
      invoiceStats: null,
      invoiceRange: 'week',
      showMessage: false
    }
  },
  mounted () {
    this.fetchUserStatus()
    this.fetchClients()
    this.fetchStatusFilters()
    this.fetchTypeFilters()
    this.fetchInvoiceStats()
    this.fetchDocuments()
  },
  methods: {
    async fetchUserStatus () {
      try {
        await ApiService.get('/user/status')
      } catch (error) {
        this.$router.push({ name: 'Login' })
      }
    },
    async fetchClients () {
      try {
        const response = await ApiService.get('/client/search', {
          params: {
            limit: -1
          }
        })
        this.clients = response.data.data
      } catch (error) {
        console.error('Error fetching clients:', error)
      }
    },
    async fetchStatusFilters () {
      try {
        const response = await ApiService.get('/file/status')
        this.statusFilters = response.data
      } catch (error) {
        console.error('Error fetching filters:', error)
      }
    },
    async fetchTypeFilters () {
      try {
        const response = await ApiService.get('/file/type')
        this.typeFilters = response.data
      } catch (error) {
        console.error('Error fetching filters:', error)
      }
    },
    async fetchInvoiceStats () {
      try {
        const response = await ApiService.get('/invoice/stats', {
          params: {
            range: this.invoiceRange
          }
        })
        this.invoiceStats = response.data
      } catch (error) {
        console.error('Error fetching invoice stats:', error)
      }
    },
    async fetchDocuments (page) {
      if (page) {
        this.currentPage = page
      }
      try {
        const response = await ApiService.get('/invoice/search', {
          params: {
            page: this.currentPage,
            keyword: this.searchParams.keyword,
            status: this.searchParams.status,
            clientUuid: this.searchParams.clientUuid,
            date: this.searchParams.date,
            type: this.searchParams.type
          }
        })
        this.invoices = response.data.data
        this.totalCount = response.data.pagination.total
      } catch (error) {
        console.error('Error fetching clients:', error)
      }
    },
    clearDate () {
      this.searchParams.date = ''
    },
    switchTab (tab) {
      this.invoiceRange = tab
      this.fetchInvoiceStats()
    },
    async updateInvoiceStatus (status, uuids = null) {
      try {
        const uuidArray = uuids
          ? (Array.isArray(uuids) ? uuids : [uuids])
          : this.selectedInvoicesUuids

        const payload = uuidArray.map(uuid => ({
          uuid,
          status
        }))

        if (!payload.length) return

        await ApiService.post('/invoice/status/update', payload)
        this.fetchDocuments()
      } catch (error) {
        console.error('Error updating invoice status:', error)
      }
    },
    selectAllInvoices () {
      const currentPageUuids = this.invoices.map(invoice => invoice.uuid)
      this.selectedInvoicesUuids = Array.from(new Set([...this.selectedInvoicesUuids, ...currentPageUuids]))
    },
    async exportPdf () {
      const zip = new JSZip()
      let hasFiles = false

      const addFilesToZip = (items) => {
        items.forEach(item => {
          if (item.fileBase64) {
            hasFiles = true
            zip.file(`${item.uuid}.pdf`, item.fileBase64, { base64: true })
          }
        })
      }

      addFilesToZip(this.selectedItems)

      if (!hasFiles) {
        this.showMessage = true
        return
      }

      const content = await zip.generateAsync({ type: 'blob' })

      const url = URL.createObjectURL(content)
      const a = document.createElement('a')
      a.href = url
      a.download = 'invoices.zip'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }
  },
  computed: {
    selectedItems () {
      return this.invoices.filter(invoice => this.selectedInvoicesUuids.includes(invoice.uuid))
    }
  }
}
</script>
